import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useTenant } from '../contexts/TenantContext'

// Generate initials from academy name
const getInitials = (name) => {
  if (!name) return 'E';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
};

// Generate a consistent gradient based on name
const getGradientFromName = (name) => {
  if (!name) return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  ];
  return gradients[Math.abs(hash) % gradients.length];
};

const SidebarBrand = () => {
  const unfoldable = useSelector((state) => state.ui?.sidebarUnfoldable)
  const { currentTenant } = useTenant();
  const [logoError, setLogoError] = useState(false);

  // Safely get tenant properties
  const tenantLogo = currentTenant?.logo;
  const tenantName = currentTenant?.name;
  const tenantSlogan = currentTenant?.slogan;
  const initials = getInitials(tenantName);
  const gradient = getGradientFromName(tenantName);
  
  // Reset logo error when tenant changes
  React.useEffect(() => {
    setLogoError(false);
  }, [tenantLogo]);

  // Show logo only if it exists and hasn't errored
  const showLogo = tenantLogo && !logoError;

  return (
    <Link to="/dashboard" className="sidebar-brand">
      <div className={`brand-full ${unfoldable ? 'd-none' : ''}`} style={{ width: '100%' }}>
        {showLogo ? (
          // Logo only - centered nicely
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-start',
            width: '100%',
            padding: '4px 0'
          }}>
            <img 
              src={tenantLogo} 
              alt={tenantName || 'Academy'} 
              style={{ 
                maxHeight: '42px', 
                maxWidth: '100%', 
                objectFit: 'contain',
                objectPosition: 'left center'
              }} 
              onError={() => setLogoError(true)}
            />
          </div>
        ) : tenantName ? (
          // No logo - show initials + text
          <>
            <div className="brand-logo">
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '700',
                fontSize: '14px',
                flexShrink: 0
              }}>
                {initials}
              </div>
            </div>
            <div className="brand-text">
              <h4 className="brand-title">{tenantName}</h4>
              {tenantSlogan && <small className="brand-subtitle">{tenantSlogan}</small>}
            </div>
          </>
        ) : (
          // Fallback - EDUNA branding
          <>
            <div className="brand-logo">
              <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="8" fill="#4f46e5" />
                <path d="M8 12h16M8 16h16M8 20h10" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <div className="brand-text">
              <h4 className="brand-title">EDUNA</h4>
              <small className="brand-subtitle">Education Platform</small>
            </div>
          </>
        )}
      </div>
      <div className={`brand-narrow ${!unfoldable ? 'd-none' : ''}`}>
        <div className="brand-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {showLogo ? (
            <img 
              src={tenantLogo} 
              alt={tenantName || 'Academy'} 
              style={{ maxHeight: '32px', maxWidth: '32px', objectFit: 'contain' }} 
              onError={() => setLogoError(true)}
            />
          ) : tenantName ? (
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              background: gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: '700',
              fontSize: '12px'
            }}>
              {initials}
            </div>
          ) : (
            <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="32" height="32" rx="8" fill="#4f46e5" />
              <text x="16" y="22" textAnchor="middle" fill="white" fontSize="18" fontWeight="bold">E</text>
            </svg>
          )}
        </div>
      </div>
    </Link>
  )
}

export default React.memo(SidebarBrand)
